import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import {
  borderGrey,
  borderPrimary,
  darkerBG,
  mediumText,
  primaryText,
  secondaryText,
  semiBoldText,
  tertiaryText,
  whiteBG,
} from '../../styling/stylingHelper';
import { createStaffUser, RESTAURANT_ROLES } from '../../services/staff/staffService';
import { useRestaurantStore } from '../../stores/appRestaurantStore';
import { InventorySlideOver, slideOverCancelClass, slideOverPrimaryClass } from '../ui/InventorySlideOver';
import FilterDropdown from '../pages/reports/hub/utilization/FilterDropdown';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  restaurants?: { id: string; name: string }[];
  createUser?: typeof createStaffUser;
}

/**
 * Unambiguous alphabet: no 0/O, 1/l/I. The generated password is EMAILED to the new staff member
 * as plaintext and retyped by hand from a phone screen, so a character they have to guess at is a
 * failed login.
 */
const PASSWORD_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
const GENERATED_PASSWORD_LENGTH = 16;
/** Mirrors the server's floor in `POST /api/staff/create`. */
const MIN_PASSWORD_LENGTH = 8;

function generatePassword(): string {
  const bytes = new Uint32Array(GENERATED_PASSWORD_LENGTH);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => PASSWORD_ALPHABET[b % PASSWORD_ALPHABET.length]).join('');
}

const SaveStaffUserModal: React.FC<Props> = ({ isOpen, onClose, onSaved, restaurants, createUser = createStaffUser }) => {
  const { t, i18n } = useTranslation();
  const storeRestaurants = useRestaurantStore((s) => s.restaurants);
  const allRestaurants = restaurants ?? storeRestaurants;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [restaurantRole, setRestaurantRole] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Flipped by Generate: the admin has to be able to read what is about to be emailed.
  const [passwordVisible, setPasswordVisible] = useState(false);

  const roleOptions = useMemo(() => RESTAURANT_ROLES.map((role) => ({ value: role, label: role })), []);

  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setPassword('');
      setRestaurantRole('');
      setSelectedIds([]);
      setError(null);
      setPasswordVisible(false);
    }
  }, [isOpen]);

  const toggle = (id: string) =>
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const handleSave = async () => {
    if (!email) {
      setError(t('staff.errorEmail'));
      return;
    }
    if (!password) {
      setError(t('staff.errorPassword'));
      return;
    }
    // The server rejects this too, but `createStaffUser` discards its error body — without this
    // check the admin would only ever see the generic save failure.
    if (password.trim().length < MIN_PASSWORD_LENGTH) {
      setError(t('staff.errorPasswordTooShort', { min: MIN_PASSWORD_LENGTH }));
      return;
    }
    if (!restaurantRole) {
      setError(t('staff.roleRequired'));
      return;
    }
    if (selectedIds.length === 0) {
      setError(t('staff.errorSelectRestaurants'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // The locale drives both outbound emails (credentials + creator confirmation).
      await createUser(email.trim(), password.trim(), restaurantRole, selectedIds, undefined, i18n.language);
      onSaved();
    } catch (err) {
      console.error(err);
      setError(t('staff.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  const inputClass = cn(
    'w-full px-[13px] py-[11px] text-[14px] leading-5 border rounded-xl focus:outline-none',
    semiBoldText,
    borderPrimary,
    whiteBG,
    primaryText
  );
  const labelClass = cn(secondaryText, mediumText, 'mb-3 text-[16px] leading-7 block');

  return (
    <InventorySlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={t('staff.addUser')}
      closeLabel={t('common.close')}
      footer={
        <>
          <button type="button" onClick={onClose} className={slideOverCancelClass} disabled={saving}>
            {t('staff.cancel')}
          </button>
          <button type="button" onClick={handleSave} className={slideOverPrimaryClass} disabled={saving}>
            {saving ? t('staff.creating') : t('staff.create')}
          </button>
        </>
      }
    >
      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className={cn('space-y-3 p-4 rounded-lg border mb-6', borderGrey, darkerBG)}>
        <div>
          <label className={labelClass} htmlFor="staff-email">
            {t('staff.email')}
          </label>
          <input
            id="staff-email"
            type="email"
            className={inputClass}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('staff.emailPlaceholder')}
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="staff-password">
            {t('staff.password')}
          </label>
          <div className="flex items-center gap-2">
            <input
              id="staff-password"
              type={passwordVisible ? 'text' : 'password'}
              className={inputClass}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => {
                setPassword(generatePassword());
                setPasswordVisible(true);
              }}
              className={cn(
                'shrink-0 px-3 h-11 rounded-xl border text-[14px] leading-5 whitespace-nowrap transition-colors hover:opacity-90',
                semiBoldText,
                borderPrimary,
                darkerBG,
                primaryText
              )}
            >
              {t('staff.generatePassword')}
            </button>
          </div>
          <p className={cn(tertiaryText, 'mt-2 text-[13px] leading-5')}>{t('staff.passwordEmailedHint')}</p>
        </div>

        <div>
          <label className={labelClass}>{t('staff.restaurantRole')}</label>
          <FilterDropdown
            label={t('staff.selectRole')}
            options={roleOptions}
            value={restaurantRole}
            onChange={setRestaurantRole}
            searchable
            fullWidth
          />
        </div>
      </div>

      <div className={cn('space-y-3 p-4 rounded-lg border', borderGrey)}>
        <p className={cn(primaryText, mediumText, 'mb-3 text-[16px] leading-7')}>{t('staff.assignRestaurants')}</p>
        <div className="max-h-[280px] overflow-y-auto space-y-3">
          {allRestaurants.map((r) => {
            const selected = selectedIds.includes(r.id);
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => toggle(r.id)}
                aria-pressed={selected}
                className={cn(
                  'w-full flex items-center justify-between gap-3 px-[15px] py-[11px] rounded-lg text-left transition-colors border',
                  selected
                    ? 'bg-cardLightGreen dark:bg-darkGreenColor-dark/10 border-darkGreenColor dark:border-darkGreenColor-dark'
                    : cn(darkerBG, borderGrey, 'hover:opacity-90')
                )}
              >
                <span className={cn('text-[16px] leading-5', semiBoldText, primaryText)}>{r.name}</span>
                <span
                  className={cn(
                    'w-5 h-5 rounded-[4px] flex items-center justify-center shrink-0 border transition-colors',
                    selected
                      ? 'bg-darkGreenColor dark:bg-darkGreenColor-dark border-darkGreenColor dark:border-darkGreenColor-dark'
                      : cn(whiteBG, borderPrimary)
                  )}
                  aria-hidden
                >
                  {selected && (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M3.33398 9.33398L5.66732 11.6673L12.6673 4.33398"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </InventorySlideOver>
  );
};

export default SaveStaffUserModal;
